// `usePhotoCollect` — TanStack mutation for photo collection (Plan 11-05).
//
// Exports three hooks that compose the Phase 11 tiered photo collection UI:
//
//   usePhotoCollect(propertyId)  — mutation; fires POST /photos/collect
//   usePhotoJobStatus(propertyId) — polling query; mirrors useJobStatus contract
//   useCreditBalance(enabled)   — lazy query; fetched only when dialog is open
//
// usePhotoCollect:
//   On success invalidates ['photoJobStatus', propertyId] AND ['property', propertyId].
//   The component toasts on mutation.error (it owns the toast context; the hook
//   just surfaces the error).
//
// usePhotoJobStatus:
//   Polls every 3s while status === 'running', stops on 'idle'|'completed'|'failed'.
//   Pitfall 14 mitigation: TanStack Query dedupes by queryKey, so React 19 StrictMode
//   double-mount fires at most one interval per ['photoJobStatus', id] key.
//   Mirror of useJobStatus refetchInterval contract (Plan 07-01).
//
// useCreditBalance:
//   Keyed ['creditBalance']. Enabled only when the Deep Dive dialog is open (D-3/D-9)
//   so balance is fetched exactly once per dialog open, NOT on every render.
//   staleTime 30s so reopening the dialog within 30s reuses the cached value
//   without a second round-trip. balance may be null — component renders "unavailable".

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  collectPhotos,
  fetchPhotoJobStatus,
  fetchCreditBalance,
} from '@/lib/api';
import type {
  PhotoCollectResponse,
  PhotoJobStatusDTO,
  CreditBalanceDTO,
} from '@/types/db';

const POLL_INTERVAL_MS = 3000;
const CREDIT_STALE_MS = 30_000;

// ---- Variable shapes -----------------------------------------------

export interface PhotoCollectVariables {
  source: 'streetview' | 'zillow' | 'deep';
}

// ---- usePhotoCollect -----------------------------------------------

/**
 * `usePhotoCollect(propertyId)` — mutation that POSTs to
 * `POST /api/properties/:id/photos/collect`.
 *
 * On success: invalidates ['photoJobStatus', propertyId] so the polling
 * hook sees the new running row, AND ['property', propertyId] so the
 * gallery refreshes once the job completes (the component polls status
 * and invalidates ['property', id] again on completion — this invalidation
 * kicks off the first status poll immediately).
 *
 * On error: `mutation.error` carries the ApiError; the component maps
 * status === 409 → "Photo collection already in progress" toast.
 */
export function usePhotoCollect(
  propertyId: number,
): UseMutationResult<PhotoCollectResponse, Error, PhotoCollectVariables> {
  const queryClient = useQueryClient();
  return useMutation<PhotoCollectResponse, Error, PhotoCollectVariables>({
    mutationFn: ({ source }) => collectPhotos(propertyId, source),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['photoJobStatus', propertyId] });
      void queryClient.invalidateQueries({ queryKey: ['property', propertyId] });
    },
  });
}

// ---- usePhotoJobStatus ---------------------------------------------

/**
 * `usePhotoJobStatus(propertyId)` — status-aware polling hook.
 *
 * Mirrors useJobStatus (Plan 07-01) contract:
 *   status === 'running' → refetch every 3s
 *   anything else        → refetchInterval returns false (stops polling)
 *   refetchIntervalInBackground: false — no background tab drain
 *
 * `propertyId` may be undefined when the component renders before the
 * mutation has fired. In that case `enabled: false` short-circuits.
 *
 * staleTime: 0 — we always want the freshest row while polling is active.
 */
export function usePhotoJobStatus(
  propertyId: number | undefined,
): UseQueryResult<PhotoJobStatusDTO, Error> {
  const isValid =
    propertyId != null && Number.isFinite(propertyId) && propertyId > 0;
  return useQuery<PhotoJobStatusDTO, Error>({
    queryKey: ['photoJobStatus', propertyId ?? -1],
    queryFn: ({ signal }) => fetchPhotoJobStatus(propertyId as number, signal),
    enabled: isValid,
    staleTime: 0,
    refetchInterval: (query) => {
      const data = query.state.data as PhotoJobStatusDTO | undefined;
      if (!data) return false;
      return data.status === 'running' ? POLL_INTERVAL_MS : false;
    },
    refetchIntervalInBackground: false,
  });
}

// ---- useCreditBalance ----------------------------------------------

/**
 * `useCreditBalance(enabled)` — fetches GET /api/credits.
 *
 * Keyed ['creditBalance']. `enabled` is passed from the component as
 * the Deep Dive dialog's `open` state — so the fetch fires exactly when
 * the dialog opens (D-3/D-9: user sees estimate + remaining balance BEFORE
 * clicking Confirm, with zero credit spend for the balance fetch itself).
 *
 * staleTime: 30s — reopening the dialog within 30s reuses the cached value
 * without a redundant round-trip.
 *
 * balance may be null (server parse failure) — component renders
 * "balance unavailable" but still permits Confirm (server-side fc_credit_check
 * 20 floor is the hard backstop — Plan 11-04 T-11-CREDIT).
 */
export function useCreditBalance(
  enabled: boolean,
): UseQueryResult<CreditBalanceDTO, Error> {
  return useQuery<CreditBalanceDTO, Error>({
    queryKey: ['creditBalance'],
    queryFn: ({ signal }) => fetchCreditBalance(signal),
    enabled,
    staleTime: CREDIT_STALE_MS,
  });
}
